import React, { useState, useMemo } from 'react';
import CreationModal from '../../components/ui/CreationModal';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CustomSnackbar from '../../components/ui/Snackbar';
import { transactionAPI } from '../../services/api';
import { getRequiredRefs, useMobileCaisseNames } from './transactionRefRules';

const TransactionRefsEditModal = ({ open, onClose, onSubmit, transaction = null }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const mobileCaisseNames = useMobileCaisseNames(transaction?.caisse?.name);

  const refFields = useMemo(() => {
    const required = getRequiredRefs(transaction, mobileCaisseNames);
    // Code client only applies to MOBILE règlements (Vérification entries carry only the BC)
    const showCodeClient = required.codeClient || transaction?.references?.some(r => r.type === 'CODE_CLIENT');
    return [
      ...(showCodeClient ? [{ name: 'codeClient', type: 'text', placeholder: 'Code client', label: 'CODE CLIENT', required: required.codeClient }] : []),
      { name: 'bc', type: 'text', placeholder: 'Numéro de bon de caisse', label: 'BON DE CAISSE', required: required.bc }
    ];
  }, [transaction, mobileCaisseNames]);

  const initialData = useMemo(() => ({
    bc: transaction?.references?.find(r => r.type === 'BC')?.value || '',
    codeClient: transaction?.references?.find(r => r.type === 'CODE_CLIENT')?.value || ''
  }), [transaction]);

  const handleSubmit = (data) => {
    const originalBc = transaction?.references?.find(r => r.type === 'BC')?.value || '';
    const originalCc = transaction?.references?.find(r => r.type === 'CODE_CLIENT')?.value || '';
    if ((data.bc?.trim() || '') === originalBc && (data.codeClient?.trim() || '') === originalCc) {
      onClose();
      return;
    }
    setFormData(data);
    setShowConfirmDialog(true);
  };

  const confirmEdit = async () => {
    if (!formData || !transaction) return;
    const refs = [];
    if (formData.bc?.trim()) refs.push({ type: 'BC', value: formData.bc.trim() });
    if (formData.codeClient?.trim()) refs.push({ type: 'CODE_CLIENT', value: formData.codeClient.trim() });
    try {
      await transactionAPI.updateRefs(transaction.id, refs);
      onSubmit(transaction.id, refs);
      setShowConfirmDialog(false);
      setFormData(null);
      onClose();
    } catch (error) {
      console.error('Error updating refs:', error);
      setSnackbar({ open: true, message: 'Erreur lors de la modification des références', severity: 'error' });
    }
  };

  const handleClose = () => {
    setFormData(null);
    onClose();
  };

  return (
    <>
      <CreationModal
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
        title="Modifier les références"
        fields={refFields}
        submitButtonText="Enregistrer"
        initialData={initialData}
        showLabels={true}
        fieldsPerRow={2}
      />

      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={confirmEdit}
        title="Confirmer la modification"
        message="Êtes-vous sûr de vouloir modifier les références de cette transaction ?"
        confirmText="Enregistrer"
        cancelText="Annuler"
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default TransactionRefsEditModal;
